// Rendering rules for share principals (Partner tier guide §6.6), shared by the
// share dialog, the who-has-access dialog and the partner Sharing tab.
//
// `principal_display` always carries a name now:
//   user    → the person
//   role    → "Role: <name>"
//   company → the company (a partner's home company → "<Partner> · direct users")
//   partner → "All <Partner> companies"

import { looksLikeHomeCompanyName } from '../../utils/tenancy';
import { SHARING_COPY } from '../../constants/partnerCopy';

export const principalType = (share) => share?.principal_display?.type || share?.principal_type;

/**
 * @param {object} share  a ShareResponse row
 * @param {{ partnerName?: string, homeCompanyId?: number }} ctx
 */
export const principalLabel = (share, { partnerName, homeCompanyId } = {}) => {
  const display = share?.principal_display || {};
  switch (principalType(share)) {
    case 'user':
      return display.name || display.email || 'User';
    case 'role':
      return `Role: ${display.name || 'role'}`;
    case 'company': {
      const id = display.id ?? share?.principal_company_id;
      const isHome =
        (homeCompanyId != null && id != null && Number(id) === Number(homeCompanyId)) ||
        looksLikeHomeCompanyName(display.name);
      if (isHome) return `${partnerName || 'Partner'} · direct users`;
      if (display.name) return display.name;
      return share?.partner_id == null ? 'Everyone in the company' : 'Company';
    }
    case 'partner':
      return SHARING_COPY.targetAll(display.name || partnerName);
    default:
      return display.name || display.email || 'Audience';
  }
};

/** Short caption for the principal's kind. */
export const principalKindLabel = (share) => {
  switch (principalType(share)) {
    case 'user':
      return 'Person';
    case 'role':
      return 'Role';
    case 'company':
      return 'Company';
    case 'partner':
      return 'All companies';
    default:
      return principalType(share) || '';
  }
};

/** A share made across a partner's companies (vs. inside one company). */
export const isPartnerShare = (share) => share?.partner_id != null;
